import React from 'react';
import OptimizedImage from '../../components/OptimizedImage';
import {
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  FileText,
  Lightbulb,
  ShieldCheck,
  Target,
} from 'lucide-react';
import type { CaseStudyContent as CaseStudyContentData, CaseStudyImage } from '../../data/caseDetails';

interface CaseStudyContentProps {
  content: CaseStudyContentData;
}

const SectionHeading: React.FC<{
  index: string;
  title: string;
  eyebrow: string;
  description?: string;
}> = ({ index, title, eyebrow, description }) => (
  <div className="mb-7">
    <div className="mb-3 flex items-center gap-3 text-sm font-black uppercase tracking-[0.18em] text-[#B7262D]">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF0F1] tracking-normal">
        {index}
      </span>
      {eyebrow}
    </div>
    <h2 className="text-2xl font-black leading-tight text-slate-950 md:text-3xl">{title}</h2>
    {description && <p className="mt-3 max-w-3xl leading-7 text-slate-600">{description}</p>}
  </div>
);

const CaseFigure: React.FC<{ image?: CaseStudyImage; eager?: boolean }> = ({ image, eager = false }) => image ? (
  <figure className="my-7 overflow-hidden rounded-2xl border border-slate-200 bg-white">
    <OptimizedImage src={image.src} alt={image.alt} width={1536} height={864}
      className="block h-auto w-full" loading={eager ? 'eager' : 'lazy'} />
    <figcaption className="border-t border-slate-100 px-4 py-3 text-xs leading-6 text-slate-500">{image.caption}</figcaption>
  </figure>
) : null;

const CaseStudyContent: React.FC<CaseStudyContentProps> = ({ content }) => (
  <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_20px_55px_rgba(88,12,18,0.09)]">
    <header className="relative overflow-hidden border-b border-[#D83A40]/10 bg-[linear-gradient(135deg,#fff_0%,#fff8f8_58%,#fbeaec_100%)] px-6 py-10 md:px-10 md:py-12">
      <div className="absolute right-0 top-0 h-56 w-56 translate-x-20 -translate-y-20 rounded-full bg-[#D83A40]/10 blur-3xl" />
      <div className="relative">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#D83A40]/20 bg-white px-4 py-2 text-xs font-bold tracking-wider text-[#B7262D] shadow-sm">
          <FileText className="h-4 w-4" />
          {content.eyebrow}
        </div>
        <p className="max-w-4xl text-lg font-medium leading-8 text-slate-700 md:text-xl md:leading-9">
          {content.summary}
        </p>
        <CaseFigure image={content.images?.hero} eager />
        {content.duration && (
          <p className="mt-5 text-sm font-semibold text-slate-500">
            项目观察周期：<span className="text-slate-900">{content.duration}</span>
          </p>
        )}
      </div>
    </header>

    <section aria-label="案例核心结果" className="grid border-b border-slate-200 sm:grid-cols-2 xl:grid-cols-4">
      {content.metrics.map((metric, index) => (
        <div
          key={metric.label}
          className={`px-6 py-7 ${index > 0 ? 'border-t border-slate-200 sm:border-t-0 sm:border-l' : ''} ${index === 2 ? 'sm:border-l-0 sm:border-t xl:border-l xl:border-t-0' : ''}`}
        >
          <div className="text-2xl font-black tracking-tight text-[#B7262D]">{metric.value}</div>
          <div className="mt-2 text-sm font-bold text-slate-900">{metric.label}</div>
          <div className="mt-1 text-xs leading-5 text-slate-500">{metric.note}</div>
        </div>
      ))}
    </section>

    <div className="space-y-16 px-6 py-10 md:px-10 md:py-14">
      <section id="case-overview">
        <SectionHeading index="01" eyebrow="Company Overview" title="企业概况" />
        <CaseFigure image={content.images?.overview} />
        <div className="grid gap-4 md:grid-cols-2">
          {content.overview.map((fact) => (
            <div key={fact.label} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[#B7262D]">
                <Building2 className="h-4 w-4" />
                {fact.label}
              </div>
              <p className="leading-7 text-slate-700">{fact.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="case-channels">
        <SectionHeading
          index="02"
          eyebrow="Before Optimization"
          title={content.channelsTitle ?? "优化前：有流量，但精准转化不足"}
          description={content.channelsDescription ?? "企业在 1688、行业展会和官网均有稳定曝光，主要问题不是“没人看”，而是关键决策信息没有被目标客户和 AI 准确识别。"}
        />
        <CaseFigure image={content.images?.channels} />
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full min-w-[760px] border-collapse text-left text-sm">
            <thead className="bg-slate-950 text-white">
              <tr>
                <th className="px-5 py-4 font-bold">{content.channelHeaders?.[0] ?? "渠道"}</th>
                <th className="px-5 py-4 font-bold">{content.channelHeaders?.[1] ?? "流量表现"}</th>
                <th className="px-5 py-4 font-bold">{content.channelHeaders?.[2] ?? "转化与信任问题"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {content.channels.map((channel) => (
                <tr key={channel.channel} className="align-top odd:bg-white even:bg-slate-50/70">
                  <th className="whitespace-nowrap px-5 py-5 font-black text-slate-900">{channel.channel}</th>
                  <td className="px-5 py-5 font-semibold text-slate-700">{channel.traffic}</td>
                  <td className="px-5 py-5 leading-7 text-slate-600">{channel.challenge}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section id="case-pain-points">
        <SectionHeading index="03" eyebrow="Core Pain Points" title={content.painPointsTitle ?? "四个核心痛点"} />
        <div className="grid gap-4 md:grid-cols-2">
          {content.painPoints.map((painPoint, index) => (
            <div key={painPoint.title} className="rounded-2xl border border-[#D83A40]/15 bg-[#FFF9F9] p-6">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#B7262D] text-sm font-black text-white">
                  {index + 1}
                </span>
                <h3 className="font-black text-slate-950">{painPoint.title}</h3>
              </div>
              <p className="leading-7 text-slate-600">{painPoint.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="case-strategy">
        <SectionHeading
          index="04"
          eyebrow="GEO Optimization Plan"
          title="GEO 优化方案"
          description={content.strategyDescription ?? "从关键词、可信内容到 AI 知识表达逐层推进，使页面既能回答真实客户问题，也便于搜索与生成式引擎提取。"}
        />
        <CaseFigure image={content.images?.strategy} />
        <div className="space-y-5">
          {content.strategies.map((strategy, index) => {
            const icons = [Target, ShieldCheck, Lightbulb];
            const Icon = icons[index] ?? Target;
            return (
              <div key={strategy.step} className="rounded-2xl border border-slate-200 p-6 md:p-7">
                <div className="flex flex-col gap-5 md:flex-row">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-black uppercase tracking-[0.16em] text-[#B7262D]">{strategy.step}</div>
                    <h3 className="mt-2 text-xl font-black text-slate-950">{strategy.title}</h3>
                    <p className="mt-3 leading-7 text-slate-600">{strategy.description}</p>
                    <ul className="mt-5 grid gap-3 md:grid-cols-2">
                      {strategy.items.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 rounded-xl bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
                          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#C92E35]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section id="case-results">
        <SectionHeading index="05" eyebrow="Optimization Results" title={content.resultsTitle ?? "优化结果：从流量曝光走向业务转化"} />
        <CaseFigure image={content.images?.results} />
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full min-w-[760px] border-collapse text-left text-sm">
            <thead className="bg-[#7E171D] text-white">
              <tr>
                <th className="px-5 py-4 font-bold">{content.resultHeaders?.[0] ?? "指标"}</th>
                <th className="px-5 py-4 font-bold">{content.resultHeaders?.[1] ?? "优化前"}</th>
                <th className="px-5 py-4 font-bold">{content.resultHeaders?.[2] ?? "优化后"}</th>
                <th className="px-5 py-4 font-bold">{content.resultHeaders?.[3] ?? "变化"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {content.results.map((result) => (
                <tr key={result.dimension} className="align-top odd:bg-white even:bg-[#FFF8F8]">
                  <th className="whitespace-nowrap px-5 py-5 font-black text-slate-900">{result.dimension}</th>
                  <td className="px-5 py-5 text-slate-600">{result.before}</td>
                  <td className="px-5 py-5 font-bold text-slate-900">{result.after}</td>
                  <td className="px-5 py-5 font-bold text-[#B7262D]">{result.change}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {content.businessOutcomes.map((outcome) => (
            <div key={outcome} className="flex items-start gap-3 rounded-2xl bg-slate-950 p-5 text-sm leading-7 text-slate-200">
              <BarChart3 className="mt-1 h-5 w-5 shrink-0 text-[#E1676C]" />
              <p>{outcome}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="case-takeaways" className="rounded-3xl bg-[linear-gradient(135deg,#7E171D_0%,#B7262D_100%)] p-7 text-white md:p-9">
        <div className="mb-6 flex items-center gap-3">
          <Lightbulb className="h-7 w-7" />
          <h2 className="text-2xl font-black">本案例的 GEO 实战逻辑</h2>
        </div>
        <CaseFigure image={content.images?.takeaways} />
        <ul className="space-y-4">
          {content.takeaways.map((takeaway) => (
            <li key={takeaway} className="flex items-start gap-3 leading-7 text-white/90">
              <ArrowRight className="mt-1 h-5 w-5 shrink-0" />
              <span>{takeaway}</span>
            </li>
          ))}
        </ul>
      </section>

      <aside className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-950" aria-label="案例数据说明">
        <strong>数据说明：</strong>{content.disclaimer}
      </aside>
    </div>
  </article>
);

export default CaseStudyContent;
